// A Findings case's story, the open case's lead: one sentence on what
// happened, then, side by side in one panel, what happened as a timeline (a
// step each, said in a few words, at its time, down one line) and the
// standards it met over who and what was involved. An actor opens Explore on it.
// A step opened
// shows beneath it the walkthrough's own words for it, the findings that cite
// its messages with the words they cite, and its actors.
//
// The steps are the trace's walkthrough (lib/traceWalkthrough.js), written
// when the case is opened, told again in short steps (lib/caseTimeline.js).
// Until they are written, or where they cannot be, the case's own findings
// stand in. The actors are read as Explore reads them over the case's traces.
// The story sets the panel's height; the actors that don't fit beside it fold
// into an "N more" (lib/caseStory.js).
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Highlight from "./ui/Highlight.jsx";
import { SkeletonBar } from "./ui/Skeleton.jsx";
import { FINDS, STEPS } from "./FlowBoard.jsx";
import { machineOf } from "./lenses/reviewSessions.jsx";
import { forgetTimeline, timelineKey, useCaseTimeline } from "../lib/caseTimeline.js";
import { groupActors, nextToFold, pauseText, stepCounts } from "../lib/caseStory.js";
import { useRecordTimes } from "../lib/recordTimes.js";
import { setCaseWords } from "../lib/caseWords.js";
import { happenedAt } from "../lib/findingsConsole.js";
import { severityRank } from "../lib/triageLite.js";
import { findingContext, maskSecrets } from "../lib/findingSummary.js";
import { findingStory } from "../lib/findingStory.js";
import { GAP_MS } from "./lenses/FindingStory.jsx";
import { flaggedOf } from "./lenses/LiteSession.jsx";
import { usePageAround } from "./lenses/TriageSessionPreview.jsx";
import { readSessionAgents } from "../lib/fleetConsoleReads.js";
import { useQuery } from "../lib/dataClient.js";
import { quoteValueIfNeeded } from "../lib/queryLang.js";
import { splitNote, startWalkthrough, stopWalkthrough, useWalkthrough } from "../lib/traceWalkthrough.js";
import "./CaseStory.css";

const lower = (v) => String(v ?? "").trim().toLowerCase();
const HM = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
const DAY_HM = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });

const PATHS = {
  user: '<circle cx="8" cy="5.2" r="2.6"/><path d="M3 13.6c.6-2.6 2.6-4 5-4s4.4 1.4 5 4"/>',
  endpoint: '<rect x="3" y="3.5" width="10" height="7" rx="1"/><path d="M1.5 12.5h13"/>',
  trace: '<rect x="2" y="3" width="12" height="10" rx="1"/><path d="m4.5 6.5 2 1.5-2 1.5M8 10h3.5"/>',
  agent: '<rect x="3" y="5" width="10" height="8" rx="1.5"/><path d="M8 5V3M6.2 9h.01M9.8 9h.01"/><circle cx="8" cy="2.4" r=".6"/>',
  model: '<rect x="4" y="4" width="8" height="8" rx="1"/><path d="M6.5 1.5v2.5M9.5 1.5v2.5M6.5 12v2.5M9.5 12v2.5M1.5 6.5H4M1.5 9.5H4M12 6.5h2.5M12 9.5h2.5"/>',
  mcp: '<path d="M10.5 2.5a3 3 0 0 0-3.6 3.9L2.5 10.8l2.7 2.7 4.4-4.4a3 3 0 0 0 3.9-3.6l-1.9 1.9-1.9-.4-.4-1.9z"/>',
  skill: '<path d="M8 2v3M8 11v3M2 8h3M11 8h3"/>',
  tool: '<path d="M2.5 4h11M2.5 8h11M2.5 12h7"/>',
  chev: '<path d="m6 3.5 4.5 4.5L6 12.5"/>',
  lock: '<rect x="3" y="7" width="10" height="7" rx="1"/><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2"/>',
  alert: '<path d="M8 2.2 14.3 13.5H1.7z"/><path d="M8 6.6v3.2M8 11.6v.2"/>',
  more: '<path d="M4 8h.01M8 8h.01M12 8h.01" stroke-width="2.4"/>',
  shield: '<path d="M8 1.8 13 3.6v4c0 3.2-2.1 5.4-5 6.6-2.9-1.2-5-3.4-5-6.6v-4z"/>',
};
function Icon({ name, size = 13 }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{ __html: PATHS[name] }} />;
}

// -- who and what was involved -------------------------------------------------

// Endpoints from the case's own findings, with their assigned identities and registered accounts; its
// traces; and, as Explore reads them over those traces, its agents (named as
// the fleet names them), models, MCP servers, skills and tools. Each is a way
// into Explore. Null while still read.
const SHOWN = 12;
const FIND_BY = Object.fromEntries(FINDS.filter((k) => k.field).map((k) => [k.field, k]));
const READS = [["models", "model"], ["mcp_servers", "mcp"], ["skills", "skill"], ["tools", "tool"]];
const REGISTERED = "Registered to the endpoint; not necessarily who ran the session";
function useCaseActors({ entry, machineFor, labels, range, canExplore }) {
  const idsKey = [...new Set(entry.view.sessions.map(lower).filter(Boolean))].join(",");
  const scope = useMemo(() => {
    const ids = idsKey ? idsKey.split(",") : [];
    return ids.length ? [{ dim: "sessions", ids, id: ids[0] }] : [];
  }, [idsKey]);
  const enabled = canExplore && scope.length > 0;
  const [agents, setAgents] = useState(null);
  useEffect(() => {
    setAgents(null);
    if (!enabled) return undefined;
    let live = true;
    readSessionAgents({ sessions: idsKey.split(","), range }).then((a) => { if (live) setAgents(a); }, () => { if (live) setAgents([]); });
    return () => { live = false; };
  }, [enabled, idsKey, range]);
  const opts = { enabled };
  const reads = [
    useQuery("getDimensionEntries", { dimKey: "models", parentScopePath: scope, limit: SHOWN, range, sparkline: false }, opts),
    useQuery("getDimensionEntries", { dimKey: "mcp_servers", parentScopePath: scope, limit: SHOWN, range, sparkline: false }, opts),
    useQuery("getDimensionEntries", { dimKey: "skills", parentScopePath: scope, limit: SHOWN, range, sparkline: false }, opts),
    useQuery("getDimensionEntries", { dimKey: "tools", parentScopePath: scope, limit: SHOWN, range, sparkline: false }, opts),
  ];
  const people = useQuery("getMachinePeople", { ids: scope[0]?.ids || [], range }, opts);
  const reading = enabled && (!agents || reads.some((r) => r.loading && !r.data) || people.loading);
  const lists = reads.map((r) => r.data?.entries);
  const listsKey = JSON.stringify(lists.map((l) => (l || []).map((x) => [x.id, x.label])));
  return useMemo(() => {
    if (reading) return null;
    const endpoints = new Map();
    for (const p of entry.view.items) {
      const id = lower(p.finding.endpoint);
      if (!id) continue;
      if (endpoints.has(id)) { endpoints.get(id).sessions.add(lower(p.finding.session)); continue; }
      const m = machineFor(p.finding.session) || machineOf(id);
      endpoints.set(id, { id, name: m?.host || `endpoint ${id.slice(0, 8)}`, user: m?.user || null, sessions: new Set([lower(p.finding.session)]) });
    }
    // A user's sessions are searched for as their endpoints' (a registered
    // user is no field of Explore's), as the welcome page does.
    const users = new Map();
    for (const e of endpoints.values()) if (e.user) users.set(e.user, [...(users.get(e.user) || []), e.id]);
    const onMachines = (ids) => {
      const each = ids.map((id) => STEPS.machine.clause(id)).filter(Boolean);
      return each.length > 1 ? `(${each.join(" OR ")})` : each[0] || null;
    };
    const sessions = idsKey ? idsKey.split(",") : [];
    return [
      ...[...endpoints.values()].flatMap((e) => {
        const person = people.data?.[e.id];
        return person?.name ? [{
          id: `identity:${e.id}`, kind: "identity", name: person.name,
          clause: STEPS.machine.clause(e.id),
          title: `${person.name} is assigned to ${e.name}`,
          context: `Assigned identity for endpoint ${e.id} (${e.name}); registered account: ${e.user || "unknown"}; traces: ${[...e.sessions].filter(Boolean).join(", ")}`,
        }] : [];
      }),
      ...[...users.entries()].map(([name, ids]) => ({ id: `user:${name}`, kind: "user", name, clause: onMachines(ids), title: REGISTERED, context: REGISTERED })),
      ...[...endpoints.values()].map((e) => ({ id: `endpoint:${e.id}`, kind: "endpoint", name: e.name, clause: STEPS.machine.clause(e.id) })),
      ...sessions.map((id, i) => ({ id: `trace:${id}`, kind: "trace", name: `Trace ${i + 1}`, clause: `session:${quoteValueIfNeeded(id)}`, title: labels.get(id) || "", context: `Trace ${id}` })),
      ...(agents || []).slice(0, SHOWN).map((a) => ({ id: `agent:${a.id}`, kind: "agent", name: a.name, clause: a.provider ? `agent:${quoteValueIfNeeded(a.provider)}` : null })),
      ...READS.flatMap(([, kind], i) => (lists[i] || []).map((x) => ({ id: `${kind}:${x.id}`, kind, name: x.label || String(x.id), clause: FIND_BY[kind]?.clause(String(x.id)) || null }))),
    ];
    // The lists are compared by what they hold, not by the read's object.
  }, [reading, entry, machineFor, labels, idsKey, agents, listsKey, people.data]);
}

function ActorRow({ actor, count, terms, canExplore, onExplore }) {
  const inner = (
    <>
      <Icon name={actor.kind === "identity" ? "user" : actor.kind} />
      <span className="case-story-name"><Highlight text={actor.name} terms={terms} /></span>
      {count ? <span className="case-story-count" title={`In ${count} step${count === 1 ? "" : "s"}`}>{count}</span> : <span />}
      <span className="case-story-chev"><Icon name="chev" size={11} /></span>
    </>
  );
  const cls = "case-story-actor";
  if (!canExplore || !onExplore || !actor.clause) return <span className={cls} title={actor.title || undefined} data-console-actor={actor.id}>{inner}</span>;
  return (
    <button type="button" className={cls} onClick={() => onExplore(actor.clause)} title={actor.kind === "identity" ? actor.title : actor.title ? `${actor.title}. Open in Explore` : "Open in Explore"} data-console-actor={actor.id}>
      {inner}
    </button>
  );
}

// Trace review's pages in Settings: where a standard the case met, and the norms and intents review read it against, are kept.
const REVIEW_SETTINGS = { standards: "/?mode=settings&setting=trace-review/standards", norms: "/?mode=settings&setting=trace-review/norms" };

function Involved({ standards, actors, counts, terms, canExplore, onExplore }) {
  const groups = useMemo(() => groupActors(actors || []), [actors]);
  const listRef = useRef(null);
  const railRef = useRef(null);
  const [hidden, setHidden] = useState(() => new Set());
  const [height, setHeight] = useState(0);
  const [open, setOpen] = useState(null);
  const countsKey = counts ? [...counts].join(",") : "";
  // A new list, new counts or a new height starts the fold again; then, after
  // every render (the list comes back when a picked step is closed), while
  // the list runs past the panel, one more folds.
  useLayoutEffect(() => { setHidden((h) => (h.size ? new Set() : h)); }, [actors, countsKey, height]);
  useLayoutEffect(() => {
    const list = listRef.current;
    const last = list?.lastElementChild;
    if (!last || last.getBoundingClientRect().bottom <= list.getBoundingClientRect().bottom + 0.5) return;
    const next = nextToFold(groups, hidden, (id) => counts?.get(id) ?? null);
    if (next) setHidden((h) => new Set([...h, next]));
  });
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || typeof ResizeObserver === "undefined") return undefined;
    let last = rail.clientHeight;
    const watch = new ResizeObserver(() => { if (rail.clientHeight !== last) { last = rail.clientHeight; setHeight(last); } });
    watch.observe(rail);
    return () => watch.disconnect();
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!e.target.closest?.(".case-story-more-wrap")) setOpen(null); };
    const key = (e) => { if (e.key === "Escape") setOpen(null); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", key); };
  }, [open]);
  const row = (a) => <ActorRow key={a.id} actor={a} count={counts ? counts.get(a.id) || 0 : null} terms={terms} canExplore={canExplore} onExplore={onExplore} />;
  return (
    <aside ref={railRef} className="case-story-rail" aria-label="Standards and who was involved" aria-busy={actors ? undefined : "true"} data-console-involved="">
      {/* The standards first, never folded; Involved folds into what is left. */}
      {standards ? (
        <section className="case-story-standards" aria-label="Standards" data-console-standard="">
          <div className="case-story-ph"><h4>Standards</h4><Link to={REVIEW_SETTINGS.norms} className="case-story-meta hover:text-[color:var(--ink)]" data-console-norms-link="">Norms and intents</Link></div>
          {standards.map((st) => st.none ? (
            <span key={st.name} className="case-story-actor is-none" title={st.help || st.name}>
              <Icon name="shield" /><span className="case-story-name"><Highlight text={st.name} terms={terms} /></span>
            </span>
          ) : (
            <Link key={st.name} to={REVIEW_SETTINGS.standards} className="case-story-actor" title={st.help || st.name} data-console-standard-link="">
              <Icon name="shield" /><span className="case-story-name"><Highlight text={st.name} terms={terms} /></span>
            </Link>
          ))}
        </section>
      ) : null}
      <div className="case-story-ph" data-console-involved-head=""><h4>Involved</h4><span className="case-story-meta">{actors ? actors.length : ""}</span></div>
      <div ref={listRef} className="case-story-groups">
        {!actors ? [3, 1, 2, 3].map((n, g) => (
          <div key={g} className="case-story-group" aria-hidden="true">
            <span className="case-story-group-label"><SkeletonBar className="h-[9px] w-[72px]" /></span>
            {Array.from({ length: n }, (_, i) => <span key={i} className="case-story-actor is-skeleton"><SkeletonBar className="h-[13px] w-[13px]" /><SkeletonBar className={["w-[70%]", "w-[55%]", "w-[80%]"][i % 3]} /></span>)}
          </div>
        )) : groups.map((g) => {
          const identities = g.actors.every((a) => a.kind === "identity");
          const folded = g.actors.filter((a) => hidden.has(a.id));
          const shown = g.actors.filter((a) => !hidden.has(a.id));
          const isOpen = open === g.label;
          const toggle = { "aria-expanded": isOpen, title: folded.map((a) => a.name).join(", "), onClick: () => setOpen(isOpen ? null : g.label) };
          const pop = isOpen ? <div className="case-story-pop" role="menu" aria-label={g.label}>{folded.map(row)}</div> : null;
          // A group folded whole is its heading alone, with how many it holds.
          if (!shown.length) {
            return (
              <div key={g.label} className="case-story-group case-story-more-wrap" data-console-involved-kind={g.label}>
                <button type="button" className="case-story-actor case-story-folded" {...toggle} data-console-involved-more={g.label}>
                  {identities ? <Icon name="user" /> : <span className="case-story-name">{g.label}</span>}<span className="case-story-count">{`${folded.length} more`}</span><span className="case-story-chev"><Icon name="chev" size={11} /></span>
                </button>
                {pop}
              </div>
            );
          }
          return (
            <div key={g.label} className="case-story-group" data-console-involved-kind={g.label}>
              {!identities ? <span className="case-story-group-label">{g.label}</span> : null}
              {shown.map(row)}
              {folded.length ? (
                <div className="case-story-more-wrap">
                  <button type="button" className="case-story-actor case-story-more" {...toggle} data-console-involved-more={g.label}>
                    <Icon name="more" /><span className="case-story-name">{`${folded.length} more`}</span><span /><span className="case-story-chev"><Icon name="chev" size={11} /></span>
                  </button>
                  {pop}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

// -- what happened ---------------------------------------------------------------

// A long story shows its first steps and its last, how it began and how it
// ended, with the ones between folded into one row that opens them.
const FOLD_HEAD = 3;
const FOLD_TAIL = 2;
const QUOTE_MAX = 280;
const clip = (t) => {
  const s = String(t ?? "").replace(/\s+/g, " ").trim();
  return s.length > QUOTE_MAX ? `${s.slice(0, QUOTE_MAX - 1)}…` : s;
};

// The case's findings that cite a message a step was told from, each with the
// words it cites there: [{ id, title, severity, quotes }].
function evidenceOf(full, findings) {
  const ids = new Set(full.map((n) => lower(n.messageId)).filter(Boolean));
  if (!ids.size) return [];
  return findings.flatMap((f) => {
    const cites = f.citations?.length ? f.citations : (f.messages || []).map((m) => ({ quote: "", messageId: m }));
    const here = cites.filter((c) => ids.has(lower(c.messageId)));
    if (!here.length) return [];
    const quotes = [...new Set(here.map((c) => clip(maskSecrets(String(c.quote || "")))).filter(Boolean))];
    return [{ id: f.id, title: maskSecrets(f.title || ""), severity: lower(f.severity), quotes }];
  });
}

// A step opened, beneath it: the walkthrough's notes it was told from, the
// findings citing their messages with the words cited, and its actors.
function StepDetail({ id, index, step, terms, actorRow }) {
  const many = step.full.length > 1;
  return (
    <div id={id} className="case-story-detail" data-console-step-full={index}>
      <div className="case-story-dsec">
        {step.full.map((n) => {
          const [head, rest] = splitNote(n.text);
          return (
            <div key={n.key} className="case-story-note">
              {many && n.time ? <span className="case-story-time">{n.time}</span> : null}
              <p><b><Highlight text={head} terms={terms} /></b>{rest ? <>{" "}<Highlight text={rest} terms={terms} /></> : null}</p>
            </div>
          );
        })}
      </div>
      {step.evidence?.length ? (
        <div className="case-story-dsec" data-console-step-evidence="">
          <span className="case-story-group-label">Evidence</span>
          {step.evidence.map((f) => (
            <div key={f.id} className="case-story-ev" data-console-evidence={f.id}>
              <span className="case-story-ev-head">
                <span className={`case-story-sev is-${f.severity}`}><Icon name="alert" size={13} /></span>
                <span><Highlight text={f.title} terms={terms} /></span>
                {f.severity ? <span className={`case-story-sev is-${f.severity}`}>{f.severity[0].toUpperCase() + f.severity.slice(1)}</span> : null}
              </span>
              {f.quotes.map((q) => <blockquote key={q} className="case-story-quote"><Highlight text={q} terms={terms} /></blockquote>)}
            </div>
          ))}
        </div>
      ) : null}
      {step.own?.length ? (
        <div className="case-story-dsec" aria-label="Who and what took part" data-console-step-actors="">
          <div className="case-story-chips">{step.own.map(actorRow)}</div>
        </div>
      ) : null}
    </div>
  );
}

// The steps down one line, each at its time. `items` are [{ key, time, text,
// actors, full, evidence, own }]; none, with `writing`, are placeholders. A
// step with `full` (the walkthrough's notes it was told from) opens in place
// with a click; `open` holds the keys of those open. Past FOLD_HEAD +
// FOLD_TAIL + 1 steps the middle folds until `unfolded`, unless the search's
// words are in it; the step the finding was flagged at stays out of the fold,
// the steps either side of it folding where there are two or more. `wide`
// makes room for times said with their day.
function Steps({ items, writing, wide, terms, open, onToggle, unfolded, onUnfold, actorRow }) {
  const baseId = useId();
  const cls = `case-story-steps ${wide ? "is-wide" : ""}`;
  if (writing) {
    return (
      <ol className={cls} aria-label="What happened, in order" aria-busy="true">
        {["w-[88%]", "w-[70%]", "w-[92%]", "w-[60%]", "w-[78%]"].map((w, i) => (
          <li key={i} className="case-story-step" aria-hidden="true">
            <span className="case-story-row">
              <SkeletonBar className="h-[11px] w-[34px] justify-self-start" /><span className="case-story-node" /><SkeletonBar className={`h-[15px] ${w}`} />
            </span>
          </li>
        ))}
      </ol>
    );
  }
  const said = (it) => terms.some((t) => t && lower(it.text).includes(lower(t)));
  const fold = !unfolded && items.length > FOLD_HEAD + FOLD_TAIL + 1 && !items.slice(FOLD_HEAD, -FOLD_TAIL).some(said);
  // What is drawn, in order: steps by index, and folds of hidden steps (`from` to `to`, inclusive).
  const parts = [];
  const run = (a, b) => {
    if (b - a + 1 >= 2) parts.push({ from: a, to: b });
    else for (let i = a; i <= b; i += 1) parts.push({ i });
  };
  if (fold) {
    const midEnd = items.length - FOLD_TAIL - 1;
    const flagAt = items.findIndex((it, i) => it.flagged && i >= FOLD_HEAD && i <= midEnd);
    for (let i = 0; i < FOLD_HEAD; i += 1) parts.push({ i });
    if (flagAt < 0) run(FOLD_HEAD, midEnd);
    else { run(FOLD_HEAD, flagAt - 1); parts.push({ i: flagAt }); run(flagAt + 1, midEnd); }
    for (let i = midEnd + 1; i < items.length; i += 1) parts.push({ i });
  } else items.forEach((_, i) => parts.push({ i }));
  // A step just after a fold says no pause: the pause was from a step out of sight.
  const afterFold = new Set(parts.flatMap((p, k) => (p.from != null && parts[k + 1]?.i != null ? [parts[k + 1].i] : [])));
  const step = (it, i) => {
    const can = it.full?.length > 0;
    const on = can && open.has(it.key);
    const Tag = can ? "button" : "div";
    const detailId = `${baseId}-${i}`;
    const picks = can ? {
      type: "button", "aria-expanded": on, "aria-controls": on ? detailId : undefined,
      onClick: () => onToggle(it.key),
      onKeyDown: (e) => { if (e.key === "Escape" && on) { e.stopPropagation(); onToggle(it.key); } },
    } : {};
    return (
      <li key={it.key} className={`case-story-step ${on ? "is-open" : ""} ${it.flagged ? "is-flagged" : ""}`}>
        {/* A long pause before the step, said as v2's timeline says it; none just after a fold. */}
        {it.gap && !afterFold.has(i) ? <span className="case-story-gap" data-console-step-gap="">{it.gap}</span> : null}
        <Tag className="case-story-row" {...picks} data-console-step={i}>
          <span className="case-story-time">{it.time}</span>
          <StepNode source={it.source} flagged={it.flagged} />
          <span className="case-story-text"><Highlight text={it.text} terms={terms} /></span>
          {can ? <span className="case-story-opens" aria-hidden="true"><Icon name="chev" size={12} /></span> : null}
        </Tag>
        {on ? <StepDetail id={detailId} index={i} step={it} terms={terms} actorRow={actorRow} /> : null}
      </li>
    );
  };
  const folded = (p) => {
    const hidden = items.slice(p.from, p.to + 1);
    const from = hidden.find((it) => it.time)?.time, to = hidden.findLast((it) => it.time)?.time;
    return (
      <li key={`fold:${p.from}`} className="case-story-step case-story-fold">
        <button type="button" className="case-story-row" onClick={onUnfold} data-console-steps-fold={hidden.length}>
          <span />
          <span className="case-story-node" aria-hidden="true" />
          <span className="case-story-fold-text">{`${hidden.length} more steps`}{from ? <span className="case-story-time">{from === to ? from : `${from}–${to}`}</span> : null}</span>
        </button>
      </li>
    );
  };
  return (
    <ol className={cls} aria-label="What happened, in order">
      {parts.map((p) => (p.from != null ? folded(p) : step(items[p.i], p.i)))}
    </ol>
  );
}

// Times on one day are said as the hour; across days, with the day (`wide`).
// Only a time the record keeps for the message itself counts: one that is
// only when Origin first received it is no time for the story. `w` is a note
// ({ happenedAt, happenedApprox }) or a record read ({ at, approx }).
function exactAt(w) {
  if (!w) return null;
  if ("happenedAt" in w) return Number.isFinite(w.happenedAt) && !w.happenedApprox ? w.happenedAt : null;
  return Number.isFinite(w.at) && !w.approx ? w.at : null;
}
const FLAGGED_HERE = "The finding was flagged here";
const SOURCE = { person: { icon: "user", said: "The person" }, agent: { icon: "agent", said: "The agent" } };

// A step's mark on the line: whose message it is, the person's or the
// agent's, else a dot; red where the finding was flagged.
function StepNode({ source, flagged }) {
  const from = SOURCE[source] || null;
  const said = [from?.said, flagged ? FLAGGED_HERE : null].filter(Boolean).join(". ");
  const tags = { ...(flagged ? { "data-console-step-flagged": "" } : {}), ...(from ? { "data-console-step-source": source } : {}) };
  if (!said) return <span className="case-story-node" aria-hidden="true" />;
  return (
    <span className={`case-story-node ${from ? "is-icon" : ""}`} role="img" aria-label={said} title={said} {...tags}>
      {from ? <Icon name={from.icon} size={12} /> : null}
    </span>
  );
}
function sayTimes(list) {
  const known = list.filter(Number.isFinite);
  const wide = new Set(known.map((ms) => new Date(ms).toDateString())).size > 1;
  const fmt = wide ? DAY_HM : HM;
  return {
    say: (ms) => (Number.isFinite(ms) ? fmt.format(ms) : ""),
    span: known.length ? [Math.min(...known), Math.max(...known)] : null, wide,
  };
}

// The finding the story is told around, as the Triage tab's summary is told
// (findingContext): the walked trace's findings, their checks and the
// stretch of the record around them, with the messages they cite.
// Undefined while the record is read; null where the trace has none.
// `verdicts` as the headline reads them, over each finding's own.
function useCaseFinding(entry, session, verdicts, standards) {
  const items = useMemo(() => (session ? entry.view.items.filter((p) => lower(p.finding.session) === session) : [])
    .map((p) => ({ ...p, verdict: verdicts?.[p.finding.id] || p.verdict })), [entry, session, verdicts]);
  const flagged = useMemo(() => flaggedOf(items.map((p) => p.finding), Object.fromEntries(items.map((p) => [p.finding.id, p.verdict]))), [items]);
  const messageIds = useMemo(() => [...new Set(flagged.flatMap((f) => f.messageIds).map(lower))], [flagged]);
  const turns = usePageAround(session || null, messageIds[0] || null);
  return useMemo(() => {
    if (!items.length) return null;
    if (turns === undefined) return undefined;
    const standardOf = (name) => (name ? { name, text: (standards || []).find((x) => x.name === name)?.help || null } : null);
    const story = turns?.length ? findingStory(turns, flagged) : null;
    const context = findingContext({
      findings: items.map((p, i) => ({ finding: p.finding, verdict: p.verdict, at: i, standard: standardOf(p.verdict?.metBy?.[0]) })),
      story,
    });
    return { context, messageIds, flag: flagOf(items, story, turns) };
  }, [items, flagged, messageIds, turns, standards]);
}

const SAID_AS = { person: "The person said", ran: "The agent ran", result: "What came back", failed: "The agent's command failed", said: "The agent said", thought: "The agent thought" };

const AGENT_ACTS = ["ran", "failed", "result", "said", "thought"];
const CALLS = ["ran", "failed"];

// The worst finding's flagged act as the record has it, a moment of its own
// in the story: the agent's message the check quoted, else its first call
// the finding cites, else anything of the agent's it cites; the person's
// words only where it cites nothing of the agent's (OR-436: a check quoting
// the person's instruction put the act on the ask). `said` is what the
// timeline's model reads of it, never shown; `text` what the step says of it
// opened; `order` places it where the walkthrough kept no time (message id ->
// its place in the page read). Null where the page does not hold it.
function flagOf(items, story, turns) {
  const worst = items.reduce((w, p, i) => (w < 0 || severityRank(lower(p.finding.severity)) > severityRank(lower(items[w].finding.severity)) ? i : w), -1);
  const moments = (story?.flags || []).find((f) => f.kind === "finding" && f.finding === worst)?.moments || [];
  const m = moments.find((x) => AGENT_ACTS.includes(x.who) && x.highlight) || moments.find((x) => CALLS.includes(x.who))
    || moments.find((x) => AGENT_ACTS.includes(x.who)) || moments[0];
  const messageId = m ? lower(turns[m.turn]?.messageId) : "";
  if (!messageId) return null;
  const said = `${SAID_AS[m.who] || "The agent"}: ${clip(maskSecrets(String(m.quote || "")))}${m.highlight ? `\nThe check quoted: ${clip(maskSecrets(m.highlight))}` : ""}`;
  return {
    messageId, at: Number.isFinite(m.at) ? m.at : null, turn: m.turn, said, text: "Flagged here.",
    order: new Map(turns.map((t, i) => [lower(t.messageId), i])),
  };
}

// The walkthrough's notes with the flagged act among them: marked on the note
// at its message, else placed by its time, else by the page's order, else last.
function withFlag(notes, flag) {
  if (!flag) return notes;
  if (notes.some((n) => lower(n.messageId) === flag.messageId)) return notes.map((n) => (lower(n.messageId) === flag.messageId ? { ...n, flagged: true, said: flag.said } : n));
  const note = { id: `flag:${flag.messageId}`, kind: "matters", messageId: flag.messageId, text: flag.text, said: flag.said, flagged: true, fromRecord: true, ...(flag.at != null ? { happenedAt: flag.at } : {}) };
  let at = -1;
  if (flag.at != null) notes.forEach((n, i) => { if (Number.isFinite(n.happenedAt) && n.happenedAt <= flag.at) at = i; });
  if (at < 0) {
    const after = notes.findIndex((n) => (flag.order.get(lower(n.messageId)) ?? -1) > flag.turn);
    at = after >= 0 ? after - 1 : notes.length - 1;
  }
  return [...notes.slice(0, at + 1), note, ...notes.slice(at + 1)];
}

/**
 * The open case's story. `headline` is its one sentence (null to say none),
 * `standards` what it met ([{ name, help, none }], null for none to say),
 * listed above Involved;
 * `access` the reader's trace access ({ status }); `canWalk` whether the
 * reader may have the walkthrough written; `onExplore(clause)` opens Explore;
 * `terms` the search's words, marked wherever the story says them.
 */
export default function CaseStory({ entry, headline, verdicts = null, standards = null, terms = [], access, labels, machineFor, range, messageAt, canWalk, canExplore, onExplore }) {
  const sensitive = access.status === "allowed";
  const lead = entry.view.items[0]?.finding || null;
  const s = sensitive ? lower(lead?.session) : "";
  const title = s ? labels.get(s) || "" : "";
  const walk = useWalkthrough(s || null);
  const walked = useMemo(() => walk.notes || [], [walk.notes]);
  const actors = useCaseActors({ entry, machineFor, labels, range, canExplore });
  const finding = useCaseFinding(entry, s, verdicts, standards);
  // The story's moments: the walkthrough's, with the flagged act as the record has it among them.
  const notes = useMemo(() => withFlag(walked, finding?.flag || null), [walked, finding]);
  const walkWithFlag = useMemo(() => ({ ...walk, notes }), [walk, notes]);
  const [attempt, setAttempt] = useState(0);
  const story = useCaseTimeline(s, walkWithFlag, actors, attempt, finding);
  // Written when the case is opened, by a reader who may have it written.
  useEffect(() => {
    if (!canWalk || !s || walk.status !== "idle" || walked.length) return;
    startWalkthrough(s, { title });
  }, [canWalk, s, title, walk.status, walked.length]);
  // Timed by the read that times findings' acts, over the window the walkthrough read.
  // A note the walkthrough kept no time for is timed from the trace's record,
  // which also says whose each step's message was.
  const times = useRecordTimes(s, [
    ...notes.filter((n) => exactAt(n) == null).map((n) => n.messageId),
    ...(story.status === "done" ? story.steps.map((st) => notes[st.moment]?.messageId) : []),
  ]);
  const [openKeys, setOpenKeys] = useState(() => new Set());
  const [unfolded, setUnfolded] = useState(false);
  useEffect(() => { setOpenKeys(new Set()); setUnfolded(false); }, [entry.id]);
  const told = story.status === "done";
  // Only a time the record keeps for the message itself; none where it keeps none.
  const timeOf = (n) => exactAt(n) ?? exactAt(n?.messageId ? times.get(lower(n.messageId)) : null);
  // Each step opens the walkthrough's notes from its moment up to the next
  // step's, the first from the walkthrough's start, so every note is under one,
  // with the case's findings that cite their messages.
  const caseFindings = entry.view.items.map((p) => p.finding);
  const written = told ? story.steps.map((st, k) => {
    const from = k ? st.moment : 0;
    const to = story.steps[k + 1]?.moment ?? notes.length;
    const slice = notes.slice(from, to);
    return {
      key: `m${st.moment}`, at: timeOf(notes[st.moment]), text: st.text, actors: st.actors,
      source: times.get(lower(notes[st.moment]?.messageId))?.source || null,
      // The flagged act read from the record says what the step wrote of it, else that it was flagged here.
      full: slice.map((n, j) => ({ key: n.id || `n${from + j}`, at: timeOf(n), text: maskSecrets(String((n.fromRecord && from + j === st.moment && st.note) || n.text || "")) })),
      // The flagged act read from the record marks its step and says so; it brings no cited words of its own.
      evidence: evidenceOf(slice.filter((n) => !n.fromRecord), caseFindings),
      holdsFlag: slice.some((n) => n.flagged),
    };
  }) : [];
  // The step that most needs a look: the one holding the flagged act as the
  // record has it; else where the walked trace's worst finding (then its
  // earliest) happened, the last step at or before its act; else the step
  // whose notes hold a message it cites. None where nothing places it.
  const worst = entry.view.items.filter((p) => lower(p.finding.session) === s)
    .map((p) => ({ f: p.finding, at: happenedAt(p.finding, messageAt) }))
    .sort((a, b) => (severityRank(lower(b.f.severity)) - severityRank(lower(a.f.severity))) || ((a.at ?? Infinity) - (b.at ?? Infinity)))[0];
  let marked = written.findIndex((it) => it.holdsFlag);
  if (marked < 0 && Number.isFinite(worst?.at)) written.forEach((it, k) => { if (Number.isFinite(it.at) && it.at <= worst.at) marked = k; });
  if (marked < 0 && worst) marked = written.findIndex((it) => it.evidence.some((e) => e.id === worst.f.id));
  if (marked >= 0) written[marked].flagged = true;
  const findings = entry.view.items.map((p) => ({
    key: p.finding.id, at: happenedAt(p.finding, messageAt), text: maskSecrets(p.finding.title || ""), actors: [],
  }));

  // What the story column says, by where the walkthrough and its short telling stand.
  const again = () => {
    if (walk.status === "done" && walked.length) { forgetTimeline(timelineKey(s, walkWithFlag, actors || [], finding)); setAttempt((n) => n + 1); }
    else startWalkthrough(s, { title });
  };
  const retry = canWalk ? <button type="button" onClick={again} data-console-walk="">Try again</button> : null;
  let state, meta = null, items = [];
  if (!sensitive) {
    state = access.status === "checking" ? "writing" : "denied";
  } else if (!lead?.session) {
    state = "none";
  } else if (told) {
    state = "told";
    items = written;
  } else if (walk.status === "running" || (walk.status === "idle" && !walked.length && canWalk) || (walked.length && (story.status === "busy" || story.status === "idle"))) {
    state = "writing";
    meta = <span className="case-story-meta" role="status">Writing from the trace…{walk.status === "running" ? <button type="button" onClick={() => stopWalkthrough(s)}>Stop</button> : null}</span>;
  } else {
    state = "findings";
    items = findings;
    const said = walk.status === "stopped" ? "Stopped. These are the findings."
      : story.status === "off" ? "No model is set up to write the timeline. These are the findings."
      : walk.status === "done" && !walked.length ? "The walkthrough found no moments. These are the findings."
      : walk.status === "error" || story.status === "error" ? "Couldn't write the timeline. These are the findings."
      : "No timeline has been written for this trace. These are the findings.";
    const bad = walk.status === "error" || story.status === "error";
    meta = <span className={`case-story-meta ${bad ? "is-bad" : ""}`} role={bad ? "alert" : "status"}>{bad ? <Icon name="alert" /> : null}{said}{story.status === "off" ? null : retry}</span>;
  }
  const all = items.flatMap((it) => [it, ...(it.full || [])]);
  const { say, span, wide } = sayTimes(all.map((x) => x.at));
  // As v2's story timeline: a time said where it changes, and a long pause in words.
  let lastSaid = null, lastAt = null;
  const shown = items.map((it) => {
    const time = say(it.at);
    const gap = lastAt != null && Number.isFinite(it.at) && it.at - lastAt >= GAP_MS ? pauseText(it.at - lastAt) : null;
    const out = {
      ...it, time: time && time !== lastSaid ? time : "", gap, full: it.full?.map((n) => ({ ...n, time: say(n.at) })),
      own: it.actors.map((id) => actors?.find((a) => a.id === id)).filter(Boolean),
    };
    if (time) lastSaid = time;
    if (Number.isFinite(it.at)) lastAt = it.at;
    return out;
  });
  const endSaid = (ms) => say(ms);
  const openable = shown.filter((it) => it.full?.length);
  const anyOpen = openable.some((it) => openKeys.has(it.key));
  const toggle = (key) => setOpenKeys((o) => { const n = new Set(o); if (!n.delete(key)) n.add(key); return n; });
  const toggleAll = () => {
    if (anyOpen) { setOpenKeys(new Set()); return; }
    setOpenKeys(new Set(openable.map((it) => it.key)));
    setUnfolded(true);
  };
  if (state === "told") {
    meta = (
      <span className="case-story-meta">
        {span ? (span[0] === span[1] ? endSaid(span[0]) : `${endSaid(span[0])}–${endSaid(span[1])}`) : null}
        {openable.length > 1 ? <button type="button" onClick={toggleAll} data-console-steps-all="">{anyOpen ? "Collapse all" : "Expand all"}</button> : null}
      </span>
    );
  }

  const counts = useMemo(() => (told ? stepCounts(story.steps) : null), [told, story.steps]);
  const actorRow = (a) => <ActorRow key={a.id} actor={a} count={null} terms={terms} canExplore={canExplore} onExplore={onExplore} />;
  // While the story is written, its sentence is too: the row's (the standard's
  // own reason) stands in only where none will be written.
  const heading = state === "writing" && sensitive;
  const summary = sensitive && told && story.summary ? story.summary : heading ? "" : headline;
  // What the case shows here, for the list's search to find it by.
  const said = [
    ...(sensitive ? [...shown.map((it) => it.text), ...notes.map((n) => maskSecrets(String(n.text || "")))] : []),
    summary,
    ...(actors || []).map((a) => a.name),
  ].join("\n");
  useEffect(() => { setCaseWords(entry.id, [said]); }, [entry.id, said]);
  const empty = (text, icon = null) => <div className="case-story-empty"><p className="case-story-lock">{icon ? <Icon name={icon} /> : null}{text}</p></div>;

  return (
    <div className="case-story" data-console-story={state}>
      {heading ? (
        <h3 className="case-story-head grid gap-[6px] py-[4px]" aria-busy="true" data-console-headline="writing">
          <SkeletonBar className="h-[15px] w-[92%]" /><SkeletonBar className="h-[15px] w-[64%]" />
        </h3>
      ) : summary ? <h3 className="case-story-head fade-in" data-console-headline=""><Highlight text={summary} terms={terms} /></h3> : null}
      <div className="case-story-panel">
        <section className="case-story-main" aria-label="What happened">
          <div className="case-story-ph"><h4>What happened</h4>{meta}</div>
          {state === "denied" ? empty(access.status === "temporarily_unavailable" ? "Trace access could not be confirmed. Reload to try again." : "Your role can't read what happened in the trace.", "lock")
            : state === "none" ? empty("This case has no trace to tell.")
            : <Steps items={shown} writing={state === "writing"} wide={wide} terms={terms} open={openKeys} onToggle={toggle} unfolded={unfolded} onUnfold={() => setUnfolded(true)} actorRow={actorRow} />}
        </section>
        <Involved standards={standards} actors={actors} counts={counts} terms={terms} canExplore={canExplore} onExplore={onExplore} />
      </div>
    </div>
  );
}
